import { FC, ReactNode } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import type { BoxProps } from '@mui/material/Box'
import type { ButtonProps } from '@mui/material/Button'
import R4RIcon, { R4RIconName } from 'components/R4RIcon'

// Shared building blocks for the "Panel Klienta 3.0" dashboard cards.

export const WidgetCard: FC<BoxProps> = ({ sx, ...props }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.06)',
      display: 'flex',
      flexDirection: 'column',
      ...sx
    }}
    {...props}
  />
)

// Card header from the Figma design: 20px icon + 16px bold navy title,
// with an optional badge aligned to the right edge.
export const WidgetCardHeader: FC<{
  icon: R4RIconName
  title: ReactNode
  badge?: ReactNode
}> = ({ icon, title, badge }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '18px 24px 14px'
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.r4r.navy }}>
        <R4RIcon name={icon} size={20} />
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: theme.r4r.navy }}>
          {title}
        </Typography>
      </Box>
      {badge}
    </Box>
  )
}

export const WidgetCardBody: FC<BoxProps> = ({ sx, ...props }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '0 24px 20px',
      ...sx
    }}
    {...props}
  />
)

// Full-width outline button with a trailing arrow, as in the Figma cards.
export const WidgetOutlineButton: FC<ButtonProps> = ({ children, sx, ...props }) => {
  const theme = useTheme()
  return (
    <Button
      fullWidth
      variant='outlined'
      endIcon={<R4RIcon name='arrowForward' size={14} />}
      sx={{
        borderColor: theme.r4r.navy,
        color: theme.r4r.navy,
        fontSize: '13px',
        fontWeight: 600,
        padding: '11px 17px',
        borderRadius: 0,
        '&:hover': { borderColor: theme.r4r.navy },
        ...sx
      }}
      {...props}>
      {children}
    </Button>
  )
}

export const WidgetBadge: FC<{ background: string; color: string; children: ReactNode }> = ({
  background,
  color,
  children
}) => (
  <Box
    sx={{
      backgroundColor: background,
      padding: '4px 12px',
      display: 'inline-flex',
      alignItems: 'center'
    }}>
    <Typography
      sx={{
        color,
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        lineHeight: 'normal',
        whiteSpace: 'nowrap'
      }}>
      {children}
    </Typography>
  </Box>
)
